import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import Button from "../../base/button/button";
import Form from "../../base/form";
import {DatePickerForm} from "../../base/date-picker";
import classnames from "classnames";
import GiftCard from "../../app-specific/payment-cards/gift-card";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import ToggleButtons from "../../base/button/toggle_buttons";


const formKeys = {
    startDate: "startDate",
    endDate: "endDate",
}

const schema = Yup.object().shape({
    [formKeys.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formKeys.endDate), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formKeys.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formKeys.startDate), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
});


const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

export const GiftCardSummaryDateOptionsValues = {
    last24Hours: 'last24Hours',
    last7Days: 'last7Days',
    last2Weeks: 'last2Weeks',
    last30Days: 'last30Days',
}

export const GiftCardSummaryDateOptionRanges = {
    last24Hours: () => ({
        startDate: DateUtils.UTCNow().subtract(1, 'day'),
        endDate: DateUtils.UTCNow(),
    }),
    last7Days: () => ({
        startDate: DateUtils.UTCNow().subtract(7, 'day'),
        endDate: DateUtils.UTCNow(),
    }),
    last2Weeks: () => ({
        startDate: DateUtils.UTCNow().subtract(2, 'week'),
        endDate: DateUtils.UTCNow(),
    }),
    last30Days: () => ({
        startDate: DateUtils.UTCNow().subtract(30, 'day'),
        endDate: DateUtils.UTCNow(),
    }),
}

const GiftCardSummarySearchSection = ({
                                          card,
                                          searchData,
                                      }) => {
    const [dateOption, setDateOption] = useState();
    const showCard = useMemo(() => !!Object.keys(card?.data ?? {})?.length && !card?.loading, [card]);

    /**@type {React.MutableRefObject<formApi>}*/
    const formApiRef = useRef();

    const initialValues = useMemo(() => ({
        ...searchData.filters,
        startDate: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters?.startDate) : GiftCardSummaryDateOptionRanges.last30Days().startDate,
        endDate: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters?.endDate) : GiftCardSummaryDateOptionRanges.last30Days().endDate,
    }), [searchData.filters])

    /**
     * Sets the date option based on the filters
     */
    useEffect(() => {
        const period = {
            startDate: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters?.startDate) : GiftCardSummaryDateOptionRanges.last30Days().startDate,
            endDate: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters?.endDate) : GiftCardSummaryDateOptionRanges.last30Days().endDate,
        }
        const ranges = Object.entries(GiftCardSummaryDateOptionRanges).map(e => ({key: e[0], range: e[1]()}));
        const found = ranges.find(e => e.range.startDate.isSame(period.startDate, 'minute') && e.range.endDate.isSame(period.endDate, 'minute'));
        if (found) {
            setDateOption(GiftCardSummaryDateOptionsValues[found.key]);
        }
    }, [searchData.filters?.startDate, searchData.filters?.endDate])


    /**
     * Changes the query of the url to the query containing the searched values so that the list pages of orders can
     * use them and search related orders.
     * @param values {any} form values
     */
    const onSubmit = useCallback((values) => {
        searchData.setFilters({
            ...values,
            [formKeys.startDate]: values[formKeys.startDate] ? DateUtils.UTCToServer(values[formKeys.startDate]) : undefined,
            [formKeys.endDate]: values[formKeys.endDate] ? DateUtils.UTCToServer(values[formKeys.endDate]) : undefined,
        });
    }, [searchData])

    /**
     * Changes the date input values based on the selected toggle button option
     */
    const changeDateByToggleButtons = useCallback((value) => {
        // refresh the same value if clicked again.
        if (!value) {
            value = dateOption;
        }
        setDateOption(value);
        let period = GiftCardSummaryDateOptionRanges[value]?.();
        if (!period)
            return;
        formApiRef.current?.change('startDate', period.startDate);
        formApiRef.current?.change('endDate', period.endDate);
    }, [dateOption])


    const predefinedDateOptions = useMemo(() => [
        {
            value: GiftCardSummaryDateOptionsValues.last24Hours,
            name: "Last 24 hours",
            disabled: false,
        },
        {
            value: GiftCardSummaryDateOptionsValues.last7Days,
            name: "Last 7 days",
            disabled: false,
        },
        {
            value: GiftCardSummaryDateOptionsValues.last2Weeks,
            name: "Last 2 weeks",
            disabled: false,
        },
        {
            value: GiftCardSummaryDateOptionsValues.last30Days,
            name: "Last 30 days",
            disabled: false,
        },
    ], [])

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({submitting, values, form}) => {
                formApiRef.current = form;
                const colClassName = 'px-2 mb-4';
                return (
                    <Row className='no-gutters align-items-start justify-content-center'>
                        <Col
                            {...(showCard && {xs: 12, lg: 5, xl: 4})}
                            className={classnames('d-flex align-items-center justify-content-center order-lg-2 transition ml-auto mt--3',
                                {'w-0 flex-grow-0': !showCard},
                                {'flex-grow-1': showCard},
                            )}
                        >
                            {
                                showCard &&
                                <div className={'p-3'}>
                                    <GiftCard
                                        card={card?.data}
                                        className={'p-3'}
                                    />
                                </div>
                            }
                        </Col>
                        <Col
                            xs={12}
                            className={classnames('no-gutters align-items-center justify-content-center row transition mr-auto order-lg-1',
                                {'col-lg-7 col-xl-8': showCard}
                            )}
                        >
                            <Col xs={12} className={colClassName}>
                                <ToggleButtons
                                    value={dateOption}
                                    onChange={(_, v) => changeDateByToggleButtons(v)}
                                    options={predefinedDateOptions}
                                    size={'small'}
                                />
                            </Col>
                            <Col xs={12} md={6} lg={12} xl={6} className={colClassName}>
                                <DatePickerForm
                                    label={'Start Date'}
                                    name={formKeys.startDate}
                                    required={required[formKeys.startDate]}
                                    maxDate={values[formKeys.endDate]}
                                    disableFuture
                                    onChange={(e) => setDateOption(null) || form.change('startDate', e)}
                                />
                            </Col>
                            <Col xs={12} md={6} lg={12} xl={6} className={colClassName}>
                                <DatePickerForm
                                    label={'End Date'}
                                    name={formKeys.endDate}
                                    required={required[formKeys.endDate]}
                                    minDate={values[formKeys.startDate]}
                                    disableFuture
                                    onChange={(e) => setDateOption(null) || form.change('endDate', e)}

                                />
                            </Col>
                            <Col xs={12}
                                 className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                                <Button
                                    disabled={!!submitting}
                                    type={'submit'}
                                    variant={'contained'}
                                    color={'primary'}
                                    className='py-3 px-8'
                                >
                                    Search
                                </Button>
                            </Col>
                        </Col>

                    </Row>
                );
            }}
        />
    );
}

export default GiftCardSummarySearchSection;
